import { useQuery } from "@tanstack/react-query";

/**
 * Server-side runtime flags from `GET /config`. Mirrors the backend
 * `AppConfig` dataclass in `controllers/config.py`. Litestar's DTO rename
 * strategy snake-cases server fields, so `discrete_only_mode` stays as-is.
 *
 * Uses raw fetch (not the typed `api` client) because /config was added
 * after the last schema regeneration. Promote to the typed client next
 * time someone runs `make gen-api`.
 */
export type AppConfig = {
  discrete_only_mode: boolean;
};

const baseUrl = import.meta.env.VITE_API_BASE_URL || "/api";

async function fetchAppConfig(): Promise<AppConfig> {
  const res = await fetch(`${baseUrl}/config/`, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`GET /config failed (${res.status})`);
  }
  return (await res.json()) as AppConfig;
}

export function useAppConfig() {
  return useQuery({
    queryKey: ["app-config"] as const,
    queryFn: fetchAppConfig,
    // Server flags rarely flip mid-session; once is enough.
    staleTime: Infinity,
    // If the endpoint isn't reachable (older deploy), let callers
    // fall back rather than spinning forever.
    retry: 1,
  });
}
